import { css } from "styled-components";
import {
  ChildrenContainer,
  NavItemContainer,
  NavItemsContainer,
  NavigationBarContainer as SharedBar,
} from "@ifgengineering/component-library/src/components/NavigationBar/styled";

/**
 * A styled component stringifies to its own class selector. Going through
 * String keeps css() generic over the wrapper's props rather than the
 * interpolated component's.
 */
const selector = (component: unknown): string => String(component);

export interface ThemedNavProps {
  /** Route id of the current item — the only DOM hook the shared bar gives us. */
  $selectedId?: string;
}

/**
 * Restyles the shared NavigationBar for the dark palette from the outside.
 *
 * The bar lives in `@ifgengineering/component-library`, which `apps/client`
 * consumes with no ThemeProvider at all, so it cannot read the theme itself.
 *
 * It follows the design-system nav: a raised plane, and a brand-green rail
 * under the current route instead of a filled pill — weight plus indicator,
 * never colour alone.
 */
export const themedNav = css<ThemedNavProps>`
  ${selector(SharedBar)} {
    background-color: ${({ theme }) => theme.colors.ui.background.raised};
  }

  ${({ theme, $selectedId }) =>
    css`
      /* The rail sits on the bar's own edge, so the row has to fill its height. */
      ${selector(NavItemsContainer)} {
        align-self: stretch;
        align-items: stretch;
        height: 100%;
      }

      ${selector(ChildrenContainer)} {
        align-items: center;
        justify-content: center;
      }

      ${selector(NavItemContainer)} {
        position: relative;
        align-self: stretch;
        background-color: ${theme.colors.ui.overlay.transparent};
        border-radius: 0;
        padding: 0 ${theme.space["2xl"]}px;
        color: ${theme.colors.ui.text.secondary};
      }

      ${selector(NavItemContainer)}:hover {
        color: ${theme.colors.ui.text.primary};
        opacity: ${theme.opacity["pressed-subtle"]};
      }

      ${$selectedId &&
      css`
        ${selector(NavItemContainer)}#${$selectedId} {
          color: ${theme.colors.ui.text.primary};
          font-weight: 600;
        }

        ${selector(NavItemContainer)}#${$selectedId}::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: ${theme.stroke.selected * 2}px;
          background: ${theme.colors.ui.interactive.cur8Brand};
        }
      `}
    `}
`;
